/**
 * RecordedTile — plays a stored footage blob for the given camera's day-slot.
 * Uses a shared playhead map so expand/collapse and layout changes resume playback.
 */
import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import api, { API } from "@/lib/api";
import { VideoOff } from "lucide-react";

const playheads = new Map();

const RecordedTile = forwardRef(function RecordedTile({ camera, footage: footageProp, onFootageChange }, ref) {
  const videoRef = useRef(null);
  const [footage, setFootage] = useState(footageProp || null);
  const [loaded, setLoaded] = useState(!!footageProp);
  const [errorCount, setErrorCount] = useState(0);
  const [buffering, setBuffering] = useState(true);
  const [srcVersion, setSrcVersion] = useState(0); // bump to force video reload on retry

  // Direct streaming URL — the browser uses HTTP Range requests for progressive
  // playback (backend serves faststart-remuxed MP4 so playback starts quickly).
  const streamUrl = useMemo(() => {
    if (!footage) return null;
    const token = localStorage.getItem("cctv_token") || "";
    const cacheBust = srcVersion > 0 ? `&_r=${srcVersion}` : "";
    return `${API}/footage/${footage.id}/stream?auth=${encodeURIComponent(token)}${cacheBust}`;
  }, [footage, srcVersion]);

  useImperativeHandle(ref, () => ({
    videoEl: () => videoRef.current,
    footage: () => footage,
  }));

  // If parent supplies footage prop, mirror to internal state (skip own fetch).
  useEffect(() => {
    if (footageProp !== undefined) {
      setFootage(footageProp || null);
      setLoaded(true);
    }
  }, [footageProp]);

  // Reset error/buffering when footage changes
  useEffect(() => {
    setErrorCount(0);
    setSrcVersion(0);
    setBuffering(!!footage);
  }, [footage]);

  // Fallback: if parent did NOT provide footageProp, do the fetch ourselves.
  useEffect(() => {
    if (footageProp !== undefined) return; // parent-driven mode
    let mounted = true;
    setFootage(null); setLoaded(false); setErrorCount(0);
    api.get(`/cameras/${camera.id}/live`).then((r) => {
      if (!mounted) return;
      const ft = r.data.footage;
      setFootage(ft); setLoaded(true);
      onFootageChange && onFootageChange(ft);
    }).catch(() => mounted && setLoaded(true));
    return () => { mounted = false; };
  }, [camera.id]);

  // Poll every 60s ONLY in standalone mode (parent handles polling otherwise)
  useEffect(() => {
    if (footageProp !== undefined) return;
    const t = setInterval(() => {
      api.get(`/cameras/${camera.id}/live`).then((r) => {
        setFootage((prev) => {
          const nf = r.data.footage;
          if (!prev && !nf) return prev;
          if (prev && nf && prev.id === nf.id) return prev;
          onFootageChange && onFootageChange(nf);
          return nf;
        });
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [camera.id, footageProp]);

  // Persist shared playhead so layout / expand transitions resume from where we left off.
  // On FIRST load (no shared position stored), seek deterministically to the wall-clock
  // time-of-day fraction so the video looks like it's been "playing all day".
  useEffect(() => {
    if (!footage || !streamUrl) return;
    const v = videoRef.current;
    if (!v) return;
    const fid = footage.id;

    const seekOnReady = () => {
      const dur = v.duration;
      const saved = playheads.get(fid);
      if (saved != null && Number.isFinite(saved) && saved > 0.2) {
        try { v.currentTime = saved; } catch (_) { /* ignore */ }
        return;
      }
      // Deterministic time-of-day mapping (IST seconds since midnight, modulo duration).
      // Same wall-clock time = same frame across all viewers/refreshes.
      if (Number.isFinite(dur) && dur > 1) {
        try {
          // IST offset is +5:30. Compute IST seconds-since-midnight.
          const now = new Date();
          const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
          const istMs = utcMs + 5.5 * 3600 * 1000;
          const ist = new Date(istMs);
          const secOfDay = ist.getHours() * 3600 + ist.getMinutes() * 60 + ist.getSeconds();
          const pos = secOfDay % dur;
          v.currentTime = pos;
          playheads.set(fid, pos);
        } catch (_) { /* ignore */ }
      }
    };
    if (v.readyState >= 1) seekOnReady();
    else v.addEventListener("loadedmetadata", seekOnReady, { once: true });

    const onTime = () => {
      if (!v.paused && !v.ended) playheads.set(fid, v.currentTime);
    };
    v.addEventListener("timeupdate", onTime);

    // Every 15 minutes, gently correct drift so long-open tabs don't diverge from wall-clock.
    const resyncTimer = setInterval(() => {
      const dur = v.duration;
      if (!Number.isFinite(dur) || dur <= 1) return;
      const now = new Date();
      const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
      const istMs = utcMs + 5.5 * 3600 * 1000;
      const ist = new Date(istMs);
      const secOfDay = ist.getHours() * 3600 + ist.getMinutes() * 60 + ist.getSeconds();
      const expected = secOfDay % dur;
      const drift = Math.abs(v.currentTime - expected);
      // Allow drift up to 30 seconds; beyond that snap back (looks like a keyframe refresh).
      if (drift > 30) {
        try { v.currentTime = expected; } catch (_) { /* ignore */ }
      }
    }, 15 * 60 * 1000);

    return () => {
      v.removeEventListener("loadedmetadata", seekOnReady);
      v.removeEventListener("timeupdate", onTime);
      clearInterval(resyncTimer);
      if (v.currentTime > 0) playheads.set(fid, v.currentTime);
    };
  }, [footage, streamUrl]);

  if (!loaded) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="mono text-[10px] text-zinc-600 uppercase tracking-widest">connecting...</div>
      </div>
    );
  }
  if (!footage || errorCount >= 3) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-700 bg-black">
        <VideoOff size={28} />
        <div className="mono text-[10px] uppercase tracking-widest">{errorCount >= 3 ? "Stream Error" : "No Signal"}</div>
      </div>
    );
  }

  const handleError = () => {
    // Silently retry up to 3 times with a fresh URL — network hiccups shouldn't
    // permanently break the tile.
    setErrorCount((c) => {
      const next = c + 1;
      if (next < 3) {
        setBuffering(true);
        setTimeout(() => setSrcVersion((v) => v + 1), 800 * next);
      }
      return next;
    });
  };

  return (
    <>
      <video
        ref={videoRef}
        key={`${footage.id}-${srcVersion}`}
        data-testid={`recorded-video-${camera.channel}`}
        src={streamUrl}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onError={handleError}
        className="w-full h-full object-cover"
      />
      {buffering && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="mono text-[10px] text-cyan-500 uppercase tracking-widest rec-blink">buffering...</div>
        </div>
      )}
    </>
  );
});

export default RecordedTile;
